import { createFileRoute } from "@tanstack/react-router";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Save,
  Power,
  PowerOff,
  X,
  Search,
  ArrowUpToLine,
  Trash2,
  Merge,
} from "lucide-react";
import {
  adminApi,
  type AdminCategoryRow,
  type AdminCustomCategoryRow,
  type UpdateCategoryPatch,
  type ListCustomCategoriesInput,
  type TriageCustomInput,
} from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { StatusPill } from "@/components/StatusPill";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authed/categories")({
  component: CategoriesPage,
});

const SORTS = [
  { value: "popular", label: "Most used" },
  { value: "recent", label: "Recently used" },
  { value: "name", label: "Name (A→Z)" },
] as const;
const PAGE_SIZE = 50;

function CategoriesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<AdminCategoryRow | null>(null);
  const [actionMessage, setActionMessage] = useState<{
    tone: "success" | "danger";
    text: string;
  } | null>(null);

  function flash(tone: "success" | "danger", text: string) {
    setActionMessage({ tone, text });
    window.setTimeout(() => setActionMessage(null), 5000);
  }

  const systemQuery = useQuery({
    queryKey: queryKeys.categories.list(),
    queryFn: async () => (await adminApi.listCategories()()).data,
  });

  const refreshSystem = () =>
    void qc.invalidateQueries({ queryKey: queryKeys.categories.all });
  const refreshCustom = () =>
    void qc.invalidateQueries({ queryKey: queryKeys.customCategories.all });

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Categories</h1>
          <p className="text-sm text-muted-foreground">
            System catalogue (left) and user-created customs awaiting triage (right).
          </p>
        </div>
      </div>

      {actionMessage ? (
        <div
          className={cn(
            "rounded-md border p-3 text-sm",
            actionMessage.tone === "success"
              ? "border-success/40 bg-success/5 text-success"
              : "border-destructive/40 bg-destructive/5 text-destructive",
          )}
        >
          {actionMessage.text}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <SystemPanel
          rows={systemQuery.data?.categories ?? []}
          isLoading={systemQuery.isLoading}
          onEdit={(row) => setEditing(row)}
        />
        <CustomQueuePanel
          systemCategories={systemQuery.data?.categories ?? []}
          flash={flash}
          onTriaged={() => {
            refreshCustom();
            refreshSystem();
          }}
        />
      </div>

      {editing ? (
        <EditDrawer
          row={editing}
          onClose={() => setEditing(null)}
          flash={(tone, text) => {
            flash(tone, text);
            if (tone === "success") refreshSystem();
          }}
        />
      ) : null}
    </div>
  );
}

function SystemPanel({
  rows,
  isLoading,
  onEdit,
}: {
  rows: AdminCategoryRow[];
  isLoading: boolean;
  onEdit: (row: AdminCategoryRow) => void;
}) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(term));
  }, [rows, search]);

  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            System catalogue ({rows.length})
          </h2>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-48 pl-8 text-xs"
              placeholder="filter…"
            />
          </div>
        </div>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No system categories.{" "}
            <span className="font-mono text-xs">
              functions/scripts/seed-categories.mjs
            </span>{" "}
            seeds from <span className="font-mono text-xs">default_categories.json</span>.
          </p>
        ) : (
          <div className="divide-y rounded-md border">
            {filtered.map((row) => (
              <button
                key={row.id}
                onClick={() => onEdit(row)}
                className="flex w-full items-center justify-between gap-3 p-3 text-left transition hover:bg-muted/50"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{row.name}</span>
                    {row.isDeleted ? (
                      <StatusPill tone="neutral">inactive</StatusPill>
                    ) : (
                      <StatusPill tone="success">active</StatusPill>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {row.id}
                  </div>
                </div>
                <div className="flex flex-col items-end text-xs text-muted-foreground">
                  <span>{row.subcategoryCount} subs</span>
                  <span className="font-mono">
                    {row.updatedAt
                      ? format(new Date(row.updatedAt), "yyyy-MM-dd")
                      : "—"}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Subcategory editing (the keyed map of {`{name, items: []}`} entries) is
          currently managed via{" "}
          <span className="font-mono">assets/offline/default_categories.json</span> +
          the seed script. Top-level fields can be edited here.
        </p>
      </CardContent>
    </Card>
  );
}

function CustomQueuePanel({
  systemCategories,
  flash,
  onTriaged,
}: {
  systemCategories: AdminCategoryRow[];
  flash: (tone: "success" | "danger", text: string) => void;
  onTriaged: () => void;
}) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<(typeof SORTS)[number]["value"]>("popular");

  const filters: ListCustomCategoriesInput = useMemo(
    () => ({
      search: search.trim() || undefined,
      sortBy,
      limit: PAGE_SIZE,
    }),
    [search, sortBy],
  );

  const query = useInfiniteQuery({
    queryKey: queryKeys.customCategories.list(filters),
    queryFn: async ({ pageParam }) => {
      const res = await adminApi.listCustomCategories()({
        ...filters,
        cursor: pageParam ?? null,
      });
      return res.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });

  const rows = query.data?.pages.flatMap((p) => p.customCategories) ?? [];
  const totalMatching = query.data?.pages[0]?.totalMatching ?? 0;

  const triage = useMutation({
    mutationFn: async (input: TriageCustomInput) =>
      (await adminApi.triageCustomCategory()(input)).data,
    onSuccess: (_data, vars) => {
      const verb =
        vars.action === "reject"
          ? "Rejected"
          : vars.action === "merge"
            ? "Merged"
            : "Promoted";
      flash("success", `${verb} custom category.`);
      onTriaged();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Custom queue ({totalMatching})
          </h2>
          <div className="flex items-center gap-2">
            <select
              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
              value={sortBy}
              onChange={(e) =>
                setSortBy(e.target.value as (typeof SORTS)[number]["value"])
              }
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-40 pl-8 text-xs"
                placeholder="filter…"
              />
            </div>
          </div>
        </div>
        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Queue empty — no user-typed customs awaiting triage.
          </p>
        ) : (
          <div className="divide-y rounded-md border">
            {rows.map((row) => (
              <CustomRow
                key={row.id}
                row={row}
                systemCategories={systemCategories}
                onTriage={(input) => triage.mutate(input)}
                isPending={triage.isPending}
              />
            ))}
          </div>
        )}
        {query.hasNextPage ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

function CustomRow({
  row,
  systemCategories,
  onTriage,
  isPending,
}: {
  row: AdminCustomCategoryRow;
  systemCategories: AdminCategoryRow[];
  onTriage: (input: TriageCustomInput) => void;
  isPending: boolean;
}) {
  const [target, setTarget] = useState<string>("");
  return (
    <div className="space-y-2 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">{row.name}</span>
            <StatusPill tone="info">×{row.usageCount}</StatusPill>
          </div>
          <div className="font-mono text-[10px] text-muted-foreground">{row.id}</div>
        </div>
        <div className="text-right text-[10px] text-muted-foreground">
          <div>created by {row.createdBy ? row.createdBy.slice(0, 8) : "—"}</div>
          <div className="font-mono">
            {row.lastUsedAt ? format(new Date(row.lastUsedAt), "yyyy-MM-dd") : "—"}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="h-7 flex-1 rounded-md border border-input bg-background px-2 text-xs"
        >
          <option value="">Select target system category…</option>
          {systemCategories
            .filter((c) => !c.isDeleted)
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
        </select>
        <Button
          size="sm"
          variant="outline"
          disabled={isPending || !target}
          onClick={() =>
            onTriage({
              id: row.id,
              action: "promote_to_system",
              mergeIntoSystemId: target,
            })
          }
          title="Add as new subcategory under target"
        >
          <ArrowUpToLine className="mr-1 h-3.5 w-3.5" /> Promote
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={isPending || !target}
          onClick={() =>
            onTriage({
              id: row.id,
              action: "merge",
              mergeIntoSystemId: target,
            })
          }
          title="Drop the custom; users typing this name will resolve to target"
        >
          <Merge className="mr-1 h-3.5 w-3.5" /> Merge
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={isPending}
          onClick={() => {
            if (window.confirm(`Reject "${row.name}" as spam/noise?`)) {
              onTriage({ id: row.id, action: "reject" });
            }
          }}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="mr-1 h-3.5 w-3.5" /> Reject
        </Button>
      </div>
    </div>
  );
}

interface EditFormState {
  name: string;
  description: string;
  icon: string;
  version: string;
}

function fromRow(r: AdminCategoryRow): EditFormState {
  return {
    name: r.name,
    description: r.description ?? "",
    icon: r.icon ?? "",
    version: r.version ?? "",
  };
}

function diffPatch(initial: EditFormState, current: EditFormState): UpdateCategoryPatch {
  const patch: UpdateCategoryPatch = {};
  if (current.name !== initial.name) patch.name = current.name;
  if (current.description !== initial.description) {
    patch.description = current.description.trim() === "" ? null : current.description;
  }
  if (current.icon !== initial.icon) {
    patch.icon = current.icon.trim() === "" ? null : current.icon.trim();
  }
  if (current.version !== initial.version) {
    patch.version = current.version.trim() === "" ? null : current.version.trim();
  }
  return patch;
}

function EditDrawer({
  row,
  onClose,
  flash,
}: {
  row: AdminCategoryRow;
  onClose: () => void;
  flash: (tone: "success" | "danger", text: string) => void;
}) {
  const initial = useMemo(() => fromRow(row), [row]);
  const [form, setForm] = useState<EditFormState>(initial);

  const update = useMutation({
    mutationFn: async (patch: UpdateCategoryPatch) =>
      (await adminApi.updateCategory()({ id: row.id, patch })).data,
    onSuccess: () => {
      flash("success", "Saved.");
      onClose();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const setActive = useMutation({
    mutationFn: async (isActive: boolean) =>
      (await adminApi.setCategoryActive()({ id: row.id, isActive })).data,
    onSuccess: ({ isActive }) => {
      flash("success", isActive ? "Reactivated." : "Deactivated.");
      onClose();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const patch = diffPatch(initial, form);
  const isDirty = Object.keys(patch).length > 0;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-background/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-xl overflow-hidden rounded-xl border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b p-5">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">{row.name}</h2>
            <p className="font-mono text-xs text-muted-foreground">{row.id}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="space-y-3 p-5">
          <Field label="Name">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field label="Description">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Icon">
              <Input
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                placeholder="material-icon name"
              />
            </Field>
            <Field label="Version">
              <Input
                value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                placeholder="e.g. 1.1.0"
              />
            </Field>
          </div>
          <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
            <div className="mb-1 font-semibold uppercase tracking-wide">
              Subcategories
            </div>
            <div>
              {row.subcategoryCount.toLocaleString()} subcategories,{" "}
              {row.itemCount.toLocaleString()} suggestion items. Currently
              maintained via{" "}
              <span className="font-mono">assets/offline/default_categories.json</span>{" "}
              + seed script.
            </div>
          </div>
        </div>
        <div className="flex flex-wrap justify-between gap-2 border-t bg-card p-4">
          <div className="flex gap-2">
            {row.isDeleted ? (
              <Button
                variant="outline"
                onClick={() => setActive.mutate(true)}
                disabled={setActive.isPending}
              >
                <Power className="mr-2 h-4 w-4" /> Reactivate
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={() => {
                  if (
                    window.confirm(
                      "Deactivate this category? Users will no longer be able to pick it.",
                    )
                  ) {
                    setActive.mutate(false);
                  }
                }}
                disabled={setActive.isPending}
              >
                <PowerOff className="mr-2 h-4 w-4" /> Deactivate
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => update.mutate(patch)}
              disabled={!isDirty || update.isPending}
            >
              <Save className="mr-2 h-4 w-4" />
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
